import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { combineLatest, distinctUntilChanged } from 'rxjs';

import { convertNumber } from '../../../../../shared/all.helpers';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import { GenericComponent } from '../generic/generic.component';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexYAxis,
    ApexGrid,
    ApexTooltip,
    ApexPlotOptions,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec3Green,
    colorAec4Orange,
    colorAec7RedLight,
    colorAec8RedDark,
} from 'src/app/shared/all.constants';
import {
    CONTINUITY_QUALITY_SUPPLY_OUTAGES_COMPARISON_CHART,
    CONTINUITY_QUALITY_SUPPLY_OUTAGES_FREQUENCY_CHART,
    CONTINUITY_QUALITY_SUPPLY_QUALITY_DECREE_CHART,
} from '../../assets/keys';

const KEYS = [
    CONTINUITY_QUALITY_SUPPLY_QUALITY_DECREE_CHART,
    CONTINUITY_QUALITY_SUPPLY_OUTAGES_FREQUENCY_CHART,
    CONTINUITY_QUALITY_SUPPLY_OUTAGES_COMPARISON_CHART,
];

interface QualityDecreeChartData {
    organizationId?: string;
    year?: string;
    xYear?: string;
    qualityDecree?: number;
}

interface OutagesFrequencyChartData {
    organizationId?: string;
    year?: string;
    xYear?: string;
    long?: number;
    short?: number;
    veryShort?: number;
}

interface OutagesComparisonChartData {
    id?: number;
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-supply-continuity-outages',
    templateUrl: './supply-continuity-outages.component.html',
    styleUrls: ['./supply-continuity-outages.component.scss'],
})
export class SupplyContinuityOutagesComponent extends GenericComponent {
    public texts = CC_CONSTANTS.continuityQuality.tabs.outages.texts;

    public qualityDecreeChartDataSignal: Signal<QualityDecreeChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_QUALITY_DECREE_CHART),
    ) as Signal<QualityDecreeChartData[]>;
    public qualityDecreeChartSeries: ApexAxisChartSeries = [];
    public qualityDecreeYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.qualityDecree.yaxisTitleLeft,
            },
        },
    ];
    public qualityDecreeChartDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null }),
    };
    public qualityDecreeChartGrid: ApexGrid = { padding: { left: 30 } };
    public qualityDecreeChartError: boolean = true;
    public qualityDecreeDashArray = [0, 8];

    public outagesFrequencyChartDataSignal: Signal<OutagesFrequencyChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_OUTAGES_FREQUENCY_CHART),
        ) as Signal<OutagesFrequencyChartData[]>;
    public outagesFrequencyChartSeries: ApexAxisChartSeries = [];
    public outagesFrequencyYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.outagesFrequency.yaxisTitleLeft,
            },
        },
    ];
    public outagesFrequencyChartDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null }),
    };
    public outagesFrequencyChartGrid: ApexGrid = { padding: { left: 30 } };
    public outagesFrequencyChartError: boolean = true;

    public outagesComparisonChartDataSignal: Signal<OutagesComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_OUTAGES_COMPARISON_CHART),
        ) as Signal<OutagesComparisonChartData[]>;
    public outagesComparisonChartSeries: ApexAxisChartSeries = [];
    public outagesComparisonChartDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public outagesComparisonCustomTooltip: ApexTooltip['custom'];
    public outagesComparisonChartError: boolean = true;
    public outagesComparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };
    public outagesComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            columnWidth: '30%',
            borderRadius: 1,
        },
    };
    public outagesComparisonYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
        },
    ];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const qualityDecreeChartData = this.qualityDecreeChartDataSignal();
            this.qualityDecreeChartError = this.checkForError(qualityDecreeChartData, [
                'xYear',
                'qualityDecree',
            ]);
            if (!this.qualityDecreeChartError) {
                this.qualityDecreeChartSeries = [
                    {
                        name: this.texts.qualityDecree.seriesTitle[0],
                        type: 'line',
                        data: qualityDecreeChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.qualityDecree,
                        })),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.qualityDecree.seriesTitle[1],
                        type: 'line',
                        data: qualityDecreeChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: this.texts.qualityDecree.thresholdExceedance,
                        })),
                        color: colorAec8RedDark,
                    },
                ];

                this.qualityDecreeChartDataLabels.enabledOnSeries = [0];
            }

            const outagesFrequencyChartData = this.outagesFrequencyChartDataSignal();
            this.outagesFrequencyChartError = this.checkForError(outagesFrequencyChartData, [
                'xYear',
                'long',
                'short',
                'veryShort',
            ]);
            if (!this.outagesFrequencyChartError) {
                this.outagesFrequencyChartSeries = [
                    {
                        name: this.texts.outagesFrequency.seriesTitle[0],
                        type: 'line',
                        data: outagesFrequencyChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.long,
                        })),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.outagesFrequency.seriesTitle[1],
                        type: 'line',
                        data: outagesFrequencyChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.short,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.outagesFrequency.seriesTitle[2],
                        type: 'line',
                        data: outagesFrequencyChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.veryShort,
                        })),
                        color: colorAec1BlueLight,
                    },
                ];
                this.outagesFrequencyChartDataLabels.enabledOnSeries = [0, 1, 2];
            }

            const outagesComparisonChartData = this.outagesComparisonChartDataSignal();
            this.outagesComparisonChartError = this.checkForError(outagesComparisonChartData, [
                'category',
                'min',
                'max',
                'mean',
            ]);
            if (!this.outagesComparisonChartError) {
                outagesComparisonChartData.sort((a, b) => a.id! - b.id!);
                this.outagesComparisonChartSeries = [
                    {
                        name: this.texts.outagesComparison.seriesTitle,
                        data: outagesComparisonChartData.map((data, index) => ({
                            x: this.texts.outagesComparison.categories[index],
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.outagesComparison.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 0,
                                    strokeHeight: 10,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },
                                {
                                    name: this.texts.outagesComparison.clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 0,
                                    strokeHeight: 10,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.outagesComparison.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.outagesComparison.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }
        });
    }

    ngOnInit() {
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.outagesComparisonCustomTooltip = this.setChartCustomTooltip(
            this.texts.outagesComparison.precision,
        );
    }

    public setChartCustomTooltip(precision: number | null = null): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const dataSerie = w.globals.initialSeries[seriesIndex];
            const serieName = dataSerie.name;
            const serieColor = dataSerie.color;
            const dataPoint = dataSerie.data[dataPointIndex];
            const min = convertNumber(dataPoint.y[0], precision, false);
            const max = convertNumber(dataPoint.y[1], precision, false);

            const meanGoal = dataPoint.goals[0];
            const meanValue = convertNumber(meanGoal.value, precision, false);
            const meanName = meanGoal.name;
            const meanColor = meanGoal.strokeColor;

            const clientValueGoal = dataPoint.goals[1];
            const clientValue = convertNumber(clientValueGoal.value, precision, false);
            const clientValueName = clientValueGoal.name;
            const clientColor = clientValueGoal.strokeColor;

            return `
                <div class="apexcharts-tooltip-title">
                    ${dataPoint.x}
                </div>

                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} coupures/us.</span>
                        </div>
                            <div class="apexcharts-tooltip-goals-group">
                            </div>
                    </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${meanValue} coupures/us.</span>
                        </div>
                            <div class="apexcharts-tooltip-goals-group">
                            </div>
                    </div>
                </div>
                    <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${clientValue} coupures/us.</span>
                        </div>
                    </div>
                </div>
            `;
        };
    }
}
